import { useLayoutEffect, useMemo } from "react";
import { useLoadedAudioId } from "../../domains/app/contexts/audio.context";
import { useTrackOnMount } from "../../hooks/use-track-on-mount";
import { gaEventRoomRecommendationShown } from "../../tools/analytics/domains/room.analytics";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PushOptInModal } from "../../components/modals/notification/PushOptInModal";
import { useNotificationPermissionState } from "../../domains/notification/hooks/useNotificationPermissionState";
import { notificationPromptDismissedAtStore } from "../../domains/user/stores/pushTokensStore";
import { hasCompletedFeedProfiling } from "../../domains/user/utils/hasCompletedFeedProfiling";
import { useStore } from "../../lib/store";
import { IconArrowDown, IconLayoutGrid, IconSearch } from "@tabler/icons-react";
import {
  isDesktopShell,
  isDesktopLayout,
  isNativeApp,
} from "../../common/hooks/useIsDesktopView";
import { DesktopDiscoveryPage } from "./desktop/DesktopDiscoveryPage";
import Header from "../../components/app/headers/Header";
import Navbar from "../../components/app/navbars/Navbar";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useFeedScrollMemory } from "../../domains/app/stores/scrollPositionsStore";
import {
  registerDiscoveryScrollContainer,
  scrollDiscoveryToTop,
} from "../../domains/app/stores/discoveryScrollStore";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import PlaylistCard from "../../components/cards/PlaylistCard";
import { PublicationCard } from "../../components/ui/PublicationCard";
import { PromoCard } from "../../components/ui/PromoCard";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { SectionLink } from "../../components/ui/SectionLink";
import { Avatar } from "../../components/ui/Avatar";
import {
  DiscoveryTabBar,
  type DiscoveryTabSelection,
} from "../../components/discovery/DiscoveryTabBar";
import { DiscoveryCongressHero } from "../../components/discovery/DiscoveryCongressHero";
import { DiscoveryInterestsDeepLink } from "../../components/discovery/DiscoveryInterestsDeepLink";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";
import type { Api } from "../../services/api";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useGetForYouRecommendedContentQuery } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { FeedEndAskGpt } from "../../components/gpt/FeedEndAskGpt";
import type { ForYouFeedItem } from "../../domains/forYou/hooks/useGetForYouRecommendedContentQuery";
import { useFeedSeed } from "../../domains/forYou/hooks/useFeedSeed";
import { usePullToRefresh } from "../../domains/forYou/hooks/usePullToRefresh";
import Loader from "../../components/Loader";
import { useViewportImpression } from "../../domains/forYou/impressions/useViewportImpression";
import type { ImpressionPayload } from "../../domains/forYou/impressions/impressionBatcher";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { getArticleTypeVariant } from "../../domains/app/utils/getArticleTypeText";
import { getRoomLogoUrl } from "../../domains/room/utils/getRoomLogoUrl";
import {
  getRoomName,
  getRoomDescription,
} from "../../domains/room/hooks/useRoomName";
import {
  roomHasNewContent,
  getLastRoomVisit,
} from "../../domains/room/utils/roomVisits";
import { hasReadArticle } from "../../domains/article/utils/articleReads";
import { usePlaylistPanelContent } from "../../domains/playlist/hooks/usePlaylistPanelContent";
import {
  useGetRecommendedPlaylistsQuery,
  useGetPersonalRecommendedPlaylistsQuery,
  seedPlaylistDetailCache,
  type ApiPlaylistModel,
} from "../../domains/playlist/hooks/useGetRecommendedPlaylistsQuery";
import { useSaveArticle } from "../../domains/user/hooks/useSaveArticle";
import { seedArticleDetailCache } from "../../features/article/hooks/useArticleData";
import { useShareArticle } from "../../domains/user/hooks/useShareArticle";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { OrganisationUid } from "../../interfaces";
import { gaEventViewItemListMixedContent } from "../../tools/analytics/domains/content.analytics";
import {
  gaEventViewItemListPlaylist,
  gaEventSelectItemPlaylist,
} from "../../tools/analytics/domains/playlist.analytics";
import PlaylistPanel from "../../components/app/PlaylistPanel";
import { authorLine } from "../../domains/article/utils/authorLine";
import { formatArticleDate } from "../../domains/app/utils/renderDate";

function formatDate(value: Date | string | null, locale: string): string {
  if (value === null) return "";
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

type FeedArticleItem = Extract<ForYouFeedItem, { content_format: "Article" }>;

type PublicArticleItem =
  Api["get"]["/webapp/public/articles"]["output"]["body"]["docs"][number];

function PublicArticleFeedItem({
  item,
  isNew,
}: {
  item: PublicArticleItem;
  isNew: boolean;
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  const { articleLink } = usePageLinks(null);
  const { activeLang } = useActiveLang();

  const journal = item.journal;
  const cardImage = item.cardImage;
  const imageUrl =
    cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;
  const meta = item.article_type === null ? "Article" : item.article_type;
  const description =
    item.tldr !== null && item.tldr.length > 0 ? item.tldr : null;

  return (
    <PublicationCard
      isInPlayer={loadedAudioId === item.id}
      title={item.title}
      journalName={journal === null ? null : journal.name}
      author={authorLine(item.primary_author, item.authors)}
      date={formatArticleDate(item.publication_date, activeLang)}
      meta={meta}
      metaVariant={getArticleTypeVariant(item.article_type)}
      access={item.isOpenAccess === true ? "open" : "closed"}
      recommendation={null}
      isNew={isNew}
      isBookmarked={false}
      onBookmark={null}
      onShare={null}
      onClick={() => {
        seedArticleDetailCache(item, null);
        history.push(articleLink(item));
      }}
      onJournalClick={
        journal === null
          ? null
          : () => {
              history.push(`/journal/${journal.uid}`);
            }
      }
      onRecommendationClick={null}
      imageUrl={imageUrl}
      description={description}
    />
  );
}

function ArticleFeedItem({
  item,
  roomId,
  isNew,
}: {
  item: FeedArticleItem;
  roomId: string | null;
  isNew: boolean;
}) {
  const loadedAudioId = useLoadedAudioId();
  const history = useHistory();
  const { articleLink } = usePageLinks(roomId);
  const { currentUser } = useCurrentUser();
  const { activeLang } = useActiveLang();
  const playlistPanel = usePlaylistPanelContent();
  const { isSaved, toggleSaveToPlaylists } = useSaveArticle(
    item,
    AnalyticsItemList.FORYOU
  );
  const { triggerShare } = useShareArticle(AnalyticsItemList.FORYOU);

  const journal = item.journal;
  const cardImage = item.cardImage;
  const imageUrl =
    cardImage === null
      ? null
      : cardImage.sizes === null
        ? cardImage.url
        : cardImage.sizes.medium.file;
  const meta = item.article_type === null ? "Article" : item.article_type;
  const description =
    item.tldr !== null && item.tldr.length > 0 ? item.tldr : null;
  // The "From {Room}" eyebrow only makes sense in the default "For you" feed.
  // When a room chip is selected (roomId !== null) every card is from it.
  const roomReco =
    roomId === null && item.recommendation.reason === "fromRoom"
      ? item.recommendation
      : null;

  return (
    <div>
      <PublicationCard
        isInPlayer={loadedAudioId === item.id}
        title={item.title}
        journalName={journal === null ? null : journal.name}
        author={authorLine(item.primary_author, item.authors)}
        date={formatArticleDate(item.publication_date, activeLang)}
        meta={meta}
        metaVariant={getArticleTypeVariant(item.article_type)}
        access={item.isOpenAccess === true ? "open" : "closed"}
        recommendation={roomReco}
        isNew={isNew}
        isBookmarked={isSaved}
        onBookmark={() => {
          void playlistPanel.openArticle(item.slug);
        }}
        onShare={
          currentUser === null
            ? null
            : () => {
                void triggerShare(item, currentUser);
              }
        }
        onClick={() => {
          seedArticleDetailCache(item, roomId);
          history.push(articleLink(item));
        }}
        onJournalClick={
          journal === null
            ? null
            : () => {
                history.push(`/journal/${journal.uid}`);
              }
        }
        onRecommendationClick={
          roomReco === null
            ? null
            : () => {
                history.push(`/room/discovery/${roomReco.roomId}`, {
                  backTo: "/discovery",
                });
              }
        }
        imageUrl={imageUrl}
        description={description}
      />
      {playlistPanel.state.status === "ready" && (
        <PlaylistPanel
          isVisible
          contentToAdd={playlistPanel.state.content}
          onClose={playlistPanel.close}
          onSave={async ({
            add,
            remove,
          }: {
            add: string[];
            remove: string[];
          }) => {
            await toggleSaveToPlaylists(item, { add, remove });
          }}
        />
      )}
    </div>
  );
}

function FeedItem({
  item,
  roomId,
  surface,
  isNew,
}: {
  item: ForYouFeedItem;
  roomId: string | null;
  /** Never `practice`: this page serves the ranked feed, topics and Rooms. */
  surface: Exclude<ImpressionPayload["surface"], "practice">;
  isNew: boolean;
}) {
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { videoArticleLink, videoStoryLink, infographicLink } =
    usePageLinks(roomId);
  // Recommender v2 (Phase 0.3): report a viewport "seen" impression once this
  // card has been >=1s in view. Hook called unconditionally; the ref only
  // attaches to rendered cards (Nectar/Audio render null → no impression).
  const impressionRef = useViewportImpression({
    contentKind: item.content_format === "Article" ? "article" : "content",
    contentId: item.id,
    surface,
  });

  const card = (() => {
    switch (item.content_format) {
      case "Article": {
        return <ArticleFeedItem item={item} roomId={roomId} isNew={isNew} />;
      }
      case "Nectar": {
        return null;
      }
      case "Video": {
        const journal = item.journal;
        const roomReco =
          roomId === null && item.recommendation.reason === "fromRoom"
            ? item.recommendation
            : null;
        const link =
          item.videoFormat === "story"
            ? videoStoryLink(item)
            : videoArticleLink(item);
        const commonProps = {
          title: item.title,
          journalName: journal === null ? null : journal.name,
          author: "",
          date: formatDate(item.publication_date, activeLang),
          meta: item.content_format,
          metaVariant: "slate" as const,
          access: "open" as const,
          recommendation: roomReco,
          isNew,
          isBookmarked: false,
          onBookmark: null,
          onShare: null,
          onJournalClick:
            journal === null
              ? null
              : () => {
                  history.push(`/journal/${journal.uid}`);
                },
          onRecommendationClick:
            roomReco === null
              ? null
              : () => {
                  history.push(`/room/discovery/${roomReco.roomId}`, {
                    backTo: "/discovery",
                  });
                },
          onClick: () => {
            history.push(link);
          },
        };
        // Videos carry no hero image in the feed.
        return (
          <PublicationCard
            isInPlayer={false}
            {...commonProps}
            imageUrl={null}
            description={null}
          />
        );
      }
      case "Infographic": {
        const journal = item.journal;
        const roomReco =
          roomId === null && item.recommendation.reason === "fromRoom"
            ? item.recommendation
            : null;
        const commonProps = {
          title: item.title,
          journalName: journal === null ? null : journal.name,
          author: "",
          date: formatDate(item.publication_date, activeLang),
          meta: item.content_format,
          metaVariant: "slate" as const,
          access: "open" as const,
          recommendation: roomReco,
          isNew,
          isBookmarked: false,
          onBookmark: null,
          onShare: null,
          onJournalClick:
            journal === null
              ? null
              : () => {
                  history.push(`/journal/${journal.uid}`);
                },
          onRecommendationClick:
            roomReco === null
              ? null
              : () => {
                  history.push(`/room/discovery/${roomReco.roomId}`, {
                    backTo: "/discovery",
                  });
                },
          onClick: () => {
            history.push(infographicLink(item));
          },
        };
        // Infographics carry no hero image in the feed.
        return (
          <PublicationCard
            isInPlayer={false}
            {...commonProps}
            imageUrl={null}
            description={null}
          />
        );
      }
      case "Audio": {
        return null;
      }
      default: {
        throw new Error(item satisfies never);
      }
    }
  })();

  if (card === null) return null;
  return <div ref={impressionRef}>{card}</div>;
}

// An item is "New" when it was created after the user last visited one of the
// rooms it belongs to — the same definition the per-room tab badge uses
// (roomHasNewContent), so a tab badge always has matching "New" cards in the
// feed (including the default "For you" feed, which spans several rooms).
function isItemNew(item: ForYouFeedItem): boolean {
  // Reading the item clears its badge (mirrors the Room pages' new indicator).
  if (hasReadArticle(item.id)) return false;
  const createdAt = new Date(item.meta.creationDate).getTime();
  return item.rooms.some((roomId) => {
    const lastVisit = getLastRoomVisit(roomId);
    return lastVisit !== null && createdAt > lastVisit;
  });
}

// The Discovery tab bar selection lives in the URL: `?room=<id>` (legacy-stable)
// or `?topic=<uid>`; absent → "For You". Room and topic are mutually exclusive.
function readSelectionFromSearch(search: string): DiscoveryTabSelection {
  const params = new URLSearchParams(search);
  const room = params.get("room");
  if (room !== null && room !== "") return { kind: "room", roomId: room };
  const topic = params.get("topic");
  if (topic !== null && topic !== "") return { kind: "topic", topicUid: topic };
  return { kind: "for-you" };
}

function buildSelectionSearch(selection: DiscoveryTabSelection): string {
  switch (selection.kind) {
    case "for-you":
      return "";
    case "room": {
      const params = new URLSearchParams();
      params.set("room", selection.roomId);
      return `?${params.toString()}`;
    }
    case "topic": {
      const params = new URLSearchParams();
      params.set("topic", selection.topicUid);
      return `?${params.toString()}`;
    }
    default:
      throw new Error(selection satisfies never);
  }
}

// Top collections carousel. Two distinct sources, hence two thin wrappers over
// one rail: in room mode the room's editorially-ordered Discovery collections
// (public endpoint), otherwise the caller's MeSH-ranked recommendations
// (private endpoint). Hidden once we know there are none — for the personalized
// rail that is a real outcome, not an error: nothing in the curated corpus
// matches this profile, and showing unrelated collections under "for you" is
// worse than showing nothing.
function CollectionsSection({
  roomId,
  topicUid,
}: {
  roomId: string | null;
  topicUid: string | null;
}): React.JSX.Element | null {
  if (roomId === null)
    return <PersonalCollectionsSection topicUid={topicUid} />;
  return <RoomCollectionsSection roomId={roomId} />;
}

function PersonalCollectionsSection({
  topicUid,
}: {
  topicUid: string | null;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { playlists, playlistIds, isLoading } =
    useGetPersonalRecommendedPlaylistsQuery(topicUid);

  return (
    <CollectionsRail
      // Remount on selection change so the horizontal scroll position resets.
      // Without it the rail keeps the offset from the previous chip: you land
      // on card 2 or 3 of the new list and, since a pinned topic's collections
      // also rank high in "for you", the visible cards can be identical — the
      // rail reads as frozen even though the list did change.
      key={topicUid === null ? "for-you" : topicUid}
      title={
        topicUid !== null ? t("Related collections") : t("Collections for you")
      }
      seeAllTo="/discovery/playlists"
      playlists={playlists}
      playlistIds={playlistIds}
      isLoading={isLoading}
    />
  );
}

function RoomCollectionsSection({
  roomId,
}: {
  roomId: string;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const { playlists, playlistIds, isLoading } =
    useGetRecommendedPlaylistsQuery(roomId);

  return (
    <CollectionsRail
      key={roomId}
      title={t("Room collections")}
      seeAllTo={`/room/playlists/${roomId}`}
      playlists={playlists}
      playlistIds={playlistIds}
      isLoading={isLoading}
    />
  );
}

function CollectionsRail({
  title,
  seeAllTo,
  playlists,
  playlistIds,
  isLoading,
}: {
  title: string;
  seeAllTo: string;
  playlists: ApiPlaylistModel[];
  playlistIds: string;
  isLoading: boolean;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  const { publicPlaylistLink } = usePageLinks(null);
  const displayed = playlists.slice(0, 8);

  useEffectOnce(() => {
    if (displayed.length > 0) {
      gaEventViewItemListPlaylist({
        playlists: displayed,
        listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
      });
    }
  }, [playlistIds]);

  if (!isLoading && displayed.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 px-5">
        <SectionTitle title={title} />
        {displayed.length >= 2 ? (
          <SectionLink to={seeAllTo}>{t("See all")}</SectionLink>
        ) : null}
      </div>
      <div className="scrollbar-hidden flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-pl-5 px-5">
        {isLoading
          ? [0, 1].map((index) => (
              <div
                key={index}
                className="min-w-0 shrink-0 basis-[95%] snap-start"
              >
                <PlaylistCard loading variant="dark" />
              </div>
            ))
          : displayed.map((playlist) => (
              <div
                key={playlist.id}
                className="min-w-0 shrink-0 basis-[95%] snap-start"
              >
                <PlaylistCard
                  playlist={playlist}
                  variant="dark"
                  onClick={() => {
                    // The recommended doc is the full detail shape — seed the
                    // detail cache so the playlist page's header paints instantly.
                    seedPlaylistDetailCache(playlist);
                    gaEventSelectItemPlaylist({
                      playlist,
                      listName: AnalyticsItemList.DISCOVERY_PLAYLISTS,
                    });
                    history.push({
                      pathname: publicPlaylistLink(playlist),
                      state: { playlist },
                    });
                  }}
                />
              </div>
            ))}
      </div>
    </div>
  );
}

/**
 * The push ask for a user who ALREADY walked the profiling conversation.
 *
 * `InterestsProfilingPage` asks whoever finishes the conversation, which only
 * ever covers users who walk it from now on. Everyone who completed it before —
 * and anyone whose OS permission was reset since — reaches Explore with the
 * question never asked, so Explore itself has to ask it.
 *
 * Entirely DERIVED, with no state of its own: every exit of the ask writes
 * `notificationPromptDismissedAtStore`, and that write is what closes the modal.
 * The store subscription is the point of `useStore` here — a raw `.get()` would
 * leave the modal up until something else happened to re-render the page.
 */
function DiscoveryPushOptInModal(): React.JSX.Element {
  const { currentUser } = useCurrentUser();
  const { permissionState } = useNotificationPermissionState();
  const [dismissedAt] = useStore(notificationPromptDismissedAtStore);

  const visible =
    currentUser !== null &&
    hasCompletedFeedProfiling(currentUser) &&
    // Answered once on this install, "Maybe later" included. Re-asking is the
    // `/onboarding/notifications` guard's job, on its own native cooldown.
    dismissedAt === null &&
    permissionState !== null &&
    // Only "we have never asked". A granted or denied permission is a decision,
    // and a web denial cannot even be re-prompted.
    permissionState.status === "notDetermined";

  return (
    <PushOptInModal
      visible={visible}
      surface="explore_entry"
      // Nothing to do next: the answer itself retires the ask.
      onDone={() => {}}
    />
  );
}

export default function DiscoveryPage(): React.JSX.Element {
  return (
    <>
      {isDesktopLayout() ? <DesktopDiscoveryPage /> : <MobileDiscoveryPage />}
      {/* A desktop browser never asks for push: a browser notification on a
          workstation is not where a clinician reads the literature. A native
          shell asks, also on a tablet that has the desktop layout. */}
      {isDesktopShell() && !isNativeApp() ? null : <DiscoveryPushOptInModal />}
    </>
  );
}

function MobileDiscoveryPage(): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { currentUser } = useCurrentUser();
  const { activeLang, contentLang } = useActiveLang();
  const isOffline = currentUser === null;
  const scrollPathname = `/discovery${location.search}`;

  const selection = readSelectionFromSearch(location.search);
  const selectedRoomId = selection.kind === "room" ? selection.roomId : null;
  const selectedTopicUid =
    selection.kind === "topic" ? selection.topicUid : null;
  const joinedRoomsQuery = useApiQuery(
    "/webapp/private/rooms",
    isOffline ? false : {}
  );
  const topicsQuery = useApiQuery(
    "/webapp/private/users/me/topics",
    isOffline ? false : {}
  );
  const topics =
    topicsQuery.state === "success"
      ? topicsQuery.body.map((topic) => ({
          uid: topic.uid,
          name: getItemTranslation(topic),
        }))
      : [];
  // Undefined while the topics load, or for a uid the reader no longer follows.
  const selectedTopic =
    selectedTopicUid === null
      ? undefined
      : topics.find((topic) => topic.uid === selectedTopicUid);
  const newContentQuery = useApiQuery(
    "/webapp/private/rooms/new-content",
    isOffline ? false : {}
  );
  const newContentByRoomId =
    newContentQuery.state === "success" ? newContentQuery.body : {};
  const rooms =
    joinedRoomsQuery.state === "success"
      ? joinedRoomsQuery.body.rooms
          .map((room) => ({
            id: room.id,
            name: getRoomName(room, activeLang),
            badge: roomHasNewContent(
              newContentByRoomId[room.id] ?? null,
              room.id
            ),
          }))
          .sort((a, b) => {
            if (a.badge === b.badge) return 0;
            return a.badge ? -1 : 1;
          })
      : [];
  const hasRooms = rooms.length > 0;

  const selectedRoom = useMemo(() => {
    if (selectedRoomId === null || joinedRoomsQuery.state !== "success")
      return null;
    return (
      joinedRoomsQuery.body.rooms.find((room) => room.id === selectedRoomId) ??
      null
    );
  }, [selectedRoomId, joinedRoomsQuery.state]);

  const { seed, refresh } = useFeedSeed();
  const feed = useGetForYouRecommendedContentQuery(
    selectedRoomId,
    selectedTopicUid,
    seed
  );

  const feedArticles = feed.contentList;
  const feedContentIds = feed.contentList.map((item) => item.id).join(",");
  useEffectOnce(() => {
    if (feed.contentList.length === 0) return;
    const environmentParams =
      selectedRoom !== null
        ? {
            type: "professional" as const,
            partner: selectedRoom.organisationModel.uid,
            partner_id: selectedRoom.organisationModel.id,
            room_id: selectedRoom.id,
          }
        : {
            type: "public" as const,
            partner: OrganisationUid.JUISCI,
            partner_id: null,
            room_id: null,
          };
    gaEventViewItemListMixedContent({
      content: feed.contentList,
      listName: AnalyticsItemList.FORYOU,
      environmentParams,
    });
  }, [feedContentIds]);

  const publicArticlesQuery = useApiInfiniteQuery(
    "/webapp/public/articles",
    isOffline
      ? { query: { limit: 20, sort: "default", language: contentLang } }
      : false
  );
  const publicArticles: PublicArticleItem[] =
    publicArticlesQuery.state === "success" ? publicArticlesQuery.docs : [];
  const recommendedRoomsQuery = useApiQuery(
    "/webapp/private/rooms/recommended",
    isOffline ? false : {}
  );

  const topRecommendedRoom =
    recommendedRoomsQuery.state === "success"
      ? (recommendedRoomsQuery.body.rooms[0] ?? null)
      : null;

  const hasContent = isOffline
    ? publicArticles.length > 0
    : feed.contentList.length > 0;
  const { scrollRef, rememberScroll, startAtTop } =
    useFeedScrollMemory<HTMLDivElement>(scrollPathname, hasContent);

  // Pull-to-refresh: pulling down from the top mints a new seed (re-keys the
  // query → refetch page 0 with a fresh rotation). Enabled for the for-you and
  // topic surfaces only — room mode is a recency archive the seed doesn't touch,
  // and offline reads from the public cache.
  const {
    contentRef,
    indicatorRef,
    status: pullStatus,
  } = usePullToRefresh({
    scrollRef,
    enabled: !isOffline && selectedRoomId === null,
    isRefreshing: feed.isFetching,
    onRefresh: refresh,
  });

  useLayoutEffect(() => {
    registerDiscoveryScrollContainer(scrollRef.current);
    return () => {
      registerDiscoveryScrollContainer(null);
    };
  }, []);

  function handleSelectChip(next: DiscoveryTabSelection) {
    const nextSearch = buildSelectionSearch(next);
    if (nextSearch === buildSelectionSearch(selection)) return;
    startAtTop(`/discovery${nextSearch}`);
    history.push({ pathname: "/discovery", search: nextSearch });
  }

  function handleAddRoom() {
    history.push("/rooms");
  }

  function handleDiscoverRoom(roomId: string) {
    // Carry the channel so a join made from this card is attributable. Without
    // it the join defaulted to "public app" and was indistinguishable from any
    // other, which left the PRD's comparator without a numerator on the very
    // side it compares against.
    history.push(`/room/discovery/${roomId}`, {
      roomDiscoveryChannel: "feed card",
    });
  }

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      background="var(--color-ds-white)"
      onScroll={rememberScroll}
      header={
        <Header
          onTitleClick={scrollDiscoveryToTop}
          title={t("Explore")}
          subtitle={
            currentUser === null
              ? undefined
              : `${t("Good morning")}, ${currentUser.firstname ?? "Doc"}`
          }
          actions={
            <IconSearch
              className="cursor-pointer text-primary-black size-6"
              onClick={() => {
                history.push("/search");
              }}
            />
          }
        />
      }
      footer={<Navbar />}
      onScrollEnd={() => {
        if (isOffline) {
          if (
            publicArticlesQuery.state === "success" &&
            publicArticlesQuery.canFetchNextPage
          ) {
            publicArticlesQuery.fetchNextPage();
          }
          return;
        }
        if (feed.canFetchNextPage) feed.fetchNextPage();
      }}
    >
      <div className="relative flex min-h-full flex-col bg-white">
        {/* Pull-to-refresh band. Height (and the content's translateY) is driven
            imperatively by usePullToRefresh — no per-frame re-render, no reflow;
            the content is pushed DOWN so the band gets its own space. Default
            collapsed via `h-0` (NOT an inline style, which a re-render would
            reset). Shows the arrow + hint while dragging, the spinner only while
            the feed is actually fetching. */}
        <div
          ref={indicatorRef}
          className="pointer-events-none absolute inset-x-0 top-0 z-10 flex h-0 items-center justify-center overflow-hidden"
        >
          {pullStatus === "refreshing" ? (
            <Loader />
          ) : pullStatus === "pulling" ? (
            <div className="flex items-center gap-2 text-ds-slate-500">
              <IconArrowDown size={18} stroke={1.5} />
              <span className="font-dm-sans text-footnote font-medium">
                {t("Pull to refresh")}
              </span>
            </div>
          ) : null}
        </div>

        <div ref={contentRef} className="flex flex-1 flex-col gap-6 pb-6">
          <DiscoveryTabBar
            topics={topics}
            rooms={rooms}
            selection={selection}
            onSelect={handleSelectChip}
            layout="mobile"
          />

          {selectedRoomId === null && selectedTopicUid === null && (
            <>
              <DiscoveryCongressHero enabled={!isOffline} />
              {currentUser !== null ? <DiscoveryInterestsDeepLink /> : null}
            </>
          )}

          {!hasRooms && !isOffline && (
            <div className="px-5">
              <PromoCard
                leading={
                  <Avatar
                    variant="icon"
                    size="md"
                    icon={<IconLayoutGrid size={16} stroke={1.5} />}
                    badge={null}
                  />
                }
                title={t("Add your first room")}
                description={t(
                  "Rooms deliver curated scientific content tailored to your specialty handpicked from leading sources."
                )}
                onClick={handleAddRoom}
              />
            </div>
          )}

          <CollectionsSection
            roomId={selectedRoomId}
            topicUid={selectedTopicUid}
          />

          <div className="flex flex-col gap-3 px-5">
            <SectionTitle
              title={
                selectedRoomId !== null
                  ? t("Room publications")
                  : selectedTopicUid !== null
                    ? t("Based on your selection")
                    : isOffline
                      ? t("What's new")
                      : t("Curated for you")
              }
            />

            <div className="flex flex-col divide-y divide-ds-slate-100 *:py-4 [&>*:first-child]:pt-0">
              {isOffline ? (
                publicArticlesQuery.state === "pending" ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <SkeletonPublicationCard
                      showImage
                      key={idx}
                      showDescription
                    />
                  ))
                ) : (
                  publicArticles.map((item) => (
                    <PublicArticleFeedItem
                      key={item.id}
                      item={item}
                      isNew={false}
                    />
                  ))
                )
              ) : feed.isLoading ? (
                Array.from({ length: 5 }).map((_, idx) => (
                  <SkeletonPublicationCard
                    showImage
                    key={idx}
                    showDescription
                  />
                ))
              ) : feedArticles.length === 0 ? (
                // Topic mode returns only topic-matching articles (no
                // cold-start fallback), so a niche topic can be legitimately
                // empty — show an honest empty state rather than a blank.
                <p className="py-6 text-center text-footnote text-ds-slate-500">
                  {selectedTopicUid !== null
                    ? t("No recent articles for this topic yet.")
                    : t("No content to show right now.")}
                </p>
              ) : (
                feedArticles.map((item) => (
                  <FeedItem
                    key={item.id}
                    item={item}
                    roomId={selectedRoomId}
                    surface={
                      selectedTopicUid !== null
                        ? "topic"
                        : selectedRoomId !== null
                          ? "room"
                          : "feed"
                    }
                    isNew={isItemNew(item)}
                  />
                ))
              )}
            </div>
            {/* Topic mode only: the for-you and room feeds have no PubMed
                query of their own. Shown once the last page is in;
                `hasNextPage` rather than `canFetchNextPage` so a background
                refetch does not blink the card. After the divided list, not
                in it: the last article keeps no rule under it. */}
            {selectedTopic !== undefined &&
              feedArticles.length > 0 &&
              !feed.hasNextPage && (
                <FeedEndAskGpt
                  feedScope={{ kind: "topic", uid: selectedTopic.uid }}
                  title={selectedTopic.name}
                  narrowing={null}
                />
              )}
          </div>

          {topRecommendedRoom !== null && (
            <div className="px-5">
              <RoomRecommendationImpression
                roomId={topRecommendedRoom.id}
                roomName={getRoomName(topRecommendedRoom, activeLang)}
              />
              <PromoCard
                leading={
                  <Avatar
                    variant="image"
                    size="md"
                    src={getRoomLogoUrl(topRecommendedRoom)}
                    alt={getRoomName(topRecommendedRoom, activeLang)}
                    imageFit="scale-down"
                    badge={null}
                  />
                }
                title={`${t("Discover")} ${getRoomName(topRecommendedRoom, activeLang)} ${t("Room")}`}
                description={
                  getRoomDescription(topRecommendedRoom, activeLang) ?? ""
                }
                onClick={() => {
                  handleDiscoverRoom(topRecommendedRoom.id);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </ScrollableLayout>
  );
}

/**
 * Counts the feed's recommended-room card as SHOWN, and renders nothing.
 *
 * A component rather than a `useTrackOnMount` call in the page body because the
 * card is conditional: a hook must run unconditionally where it is called, and
 * an impression must only be counted when the card genuinely reached the
 * screen. Mounting is that moment.
 *
 * This is the denominator half of the PRD's channel comparison — the GPT card
 * has had its impressions from the start, the feed card had none, so the two
 * conversion rates were not comparable at all.
 */
function RoomRecommendationImpression({
  roomId,
  roomName,
}: {
  roomId: string;
  roomName: string;
}): null {
  useTrackOnMount(
    "room-recommendation-shown",
    // Keyed per PAGE, not per GA surface: this card and the practice feed's
    // both report `surface: "feed"`, but they are two cards on two pages and a
    // reader can be shown, and open, either.
    { status: "shown", key: `discovery:${roomId}` },
    () => {
      gaEventRoomRecommendationShown({ roomId, roomName, surface: "feed" });
    }
  );
  return null;
}
